import { db } from '../config/firebase';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { checkIsCurrentUserAdmin } from './adminService';
import AppDeploymentsService from './appDeploymentsService';

/**
 * Process Google profile photo URL to get optimized version
 * @param {Object} user - Firebase user object
 * @returns {string} - Optimized photo URL
 */
export const getOptimizedProfilePhoto = (user) => {
  if (!user) return '';
  
  let photoURL = user.photoURL || '';
  
  // If photoURL exists and is from Google, ensure it has the correct format
  if (photoURL && photoURL.includes('googleusercontent.com')) {
    // Get the base URL without parameters
    const baseURL = photoURL.split('=')[0];
    
    // For Google profile photos, use a specific size (256x256 with crop)
    photoURL = baseURL + '=s256-c';
    
    console.log('Optimized Google photo URL:', photoURL);
  } else if (!photoURL && user.providerData && user.providerData.length > 0) {
    // If no direct photoURL, try to get it from the Google provider data
    const googleProvider = user.providerData.find(provider => 
      provider.providerId === 'google.com'
    );
    
    if (googleProvider && googleProvider.photoURL) {
      // Process the provider photo URL if it's from Google
      const providerPhotoURL = googleProvider.photoURL;
      
      if (providerPhotoURL.includes('googleusercontent.com')) {
        const baseURL = providerPhotoURL.split('=')[0];
        photoURL = baseURL + '=s256-c';
      } else {
        photoURL = providerPhotoURL;
      }
      
      console.log('Using provider photo URL:', photoURL);
    }
  }
  
  return photoURL;
};

/**
 * Fetch all users and count their applications
 * @returns {Promise<Array>} - Array of user objects with application counts
 */
export const getAllUsers = async () => {
  try {
    // Check if user is admin
    const isAdmin = await checkIsCurrentUserAdmin();
    
    // Get all applications to count them per user (we need this in both cases)
    const appsQuery = query(collection(db, 'userApps'));
    const appsSnapshot = await getDocs(appsQuery);
    
    // Count applications per user
    const appCountByUser = {};
    appsSnapshot.forEach(doc => {
      const app = doc.data();
      const userId = app.userId;
      
      if (userId) {
        if (!appCountByUser[userId]) {
          appCountByUser[userId] = 0;
        }
        appCountByUser[userId]++;
      }
    });
    
    if (isAdmin) {
      // Admin users can access all users from Auth
      try {
        const getAllUsersFunction = httpsCallable(functions, 'getAllUsers');
        const result = await getAllUsersFunction();
        
        // Add application counts to the users returned from Auth
        const authUsers = result.data.users || [];
        return authUsers.map(user => ({
          ...user,
          applicationsCount: appCountByUser[user.uid] || 0,
          // Map isDisabled from Auth to isActive for UI
          isActive: !user.isDisabled
        }));
      } catch (adminError) {
        console.error('Error getting users from Admin SDK:', adminError);
        // Fall back to Firestore method if Cloud Function fails
      }
    }
    
    // Regular method using Firestore (fallback for non-admins or if admin method fails)
    // Get all users from the users collection
    const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const usersSnapshot = await getDocs(usersQuery);
    
    // Map users with their application counts
    const users = usersSnapshot.docs.map(doc => {
      const userData = doc.data();
      const userId = doc.id;
      
      return {
        uid: userId,
        displayName: userData.displayName || 'No Name',
        email: userData.email || '',
        createdAt: userData.createdAt || null,
        lastLogin: userData.lastLogin || null,
        isActive: userData.isActive !== false, // default to active if not specified
        applicationsCount: appCountByUser[userId] || 0,
        photoURL: userData.photoURL || ''
      };
    });
    
    return users;
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

/**
 * Fetch all applications for a specific user
 * @param {string} userId - The ID of the user
 * @returns {Promise<Array>} - Array of application objects
 */
export const getUserApplications = async (userId) => {
  try {
    console.log('Fetching applications for user:', userId);
    
    // Check if user is admin
    const isAdmin = await checkIsCurrentUserAdmin();
    console.log('Is admin:', isAdmin);
    
    // Get applications for the specific user
    const appsQuery = query(
      collection(db, 'userApps'),
      where('userId', '==', userId)
    );
    
    const appsSnapshot = await getDocs(appsQuery);
    console.log('Found applications:', appsSnapshot.size);
    
    // Create instance of AppDeploymentsService
    const appDeploymentsService = new AppDeploymentsService();
    
    // Map applications with their data and sort them
    const applications = await Promise.all(
      appsSnapshot.docs.map(async (doc) => {
        const appData = doc.data();
        console.log('Application data:', appData);
        
        // Fetch deployment status if fileId exists
        let deploymentStatus = null;
        if (appData.fileId) {
          try {
            deploymentStatus = await appDeploymentsService.getDeploymentByFileId(appData.fileId);
          } catch (err) {
            console.error('Error fetching deployment status:', err);
          }
        }
        
        return {
          id: doc.id,
          name: appData.fileName || 'Unnamed Application',
          status: appData.status || 'unknown',
          statusApp: appData.statusApp || 'unknown',
          createdAt: appData.uploadedAt || null,
          updatedAt: appData.lastUpdated || null,
          fileSize: appData.fileSize || 0,
          fileType: appData.appType || 'unknown',
          fileId: appData.fileId,
          deploymentStatus // Add deployment status to the application object
        };
      })
    );
    
    // Sort applications by createdAt in descending order
    applications.sort((a, b) => {
      const dateA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : a.createdAt;
      const dateB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : b.createdAt;
      return dateB - dateA;
    });
    
    console.log('Mapped applications with deployment status:', applications);
    return applications;
  } catch (error) {
    console.error('Error getting user applications:', error);
    throw error;
  }
}; 